import { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AppKey, Url } from '@atlassiansox/bxpkit-core';
import 'isomorphic-fetch';
import Cookies from 'js-cookie';
import type {
  AnalyticsTrackType,
  ConsentsObject,
  ProductPayloadObject
} from '../../model';
import { ProductKeys } from '../../model';

import { ResendEmailType, SubmitErrorType } from '../../model';
import {
  trackGoToBitbucket,
  trackGoToJSW,
  trackProgressPollError,
  trackProgressPollInvalid,
  trackProgressPollMissing,
  trackProgressUri,
  trackProgressUriErr,
  trackSubmitSuccess,
  trackSubmitSuccessProducts,
  trackWaitedSignupPageSubmitMoreThan15SOptions,
  trackWaitedSignupPageSubmitMoreThan60S
} from './analytics';

const URL_JSW_STAGE = 'jira-dev.com';
const URL_JSW_PROD = 'atlassian.net';

const URL_BB_STAGE = 'https://integration.bb-inf.net';
const URL_BB_PROD = 'https://bitbucket.org';

export const URL_BITBUCKET_BUNDLE_PAGE = 'software/bitbucket/bundle';

export type FormSubmitN2NType = {
  readonly payload: FormPayloadType | FormPayloadActivateProducts;
  readonly wacEnvUrl: string;
  readonly confirmationPageUrl: string;
  readonly checkEmailPageUrl: string;
  readonly isStaging: boolean;
  readonly isLoggedIn?: boolean;
  readonly isJswIncluded?: boolean | null;
  readonly isOpsgenieIncluded?: boolean | null;
  readonly dataRegion?: string | null;
  readonly setPollingLoaderShown: (val: boolean) => void;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly setSubmitError: (val: SubmitErrorType) => void;
  readonly resendEmail?: ResendEmailScreenType;
  readonly solutionStatsEvent?: UIAnalyticsEvent;
  readonly hideUserSegmentation?: boolean;
  readonly isAppProvisioningEligible?: boolean;
};

export type FormSubmitDeferredPasswordType = {
  readonly email: string | null;
  readonly displayName: string | null | undefined;
  readonly continueUrl: string | null;
  readonly isStaging: boolean | null;
  readonly setPollingLoaderShown: (val: boolean) => void;
  readonly analyticsTrack: AnalyticsTrackType;
  readonly setSubmitError: (val: SubmitErrorType) => void;
};

export type ResendEmailScreenType = {
  readonly enableResendEmail: boolean;
  readonly setShowResendEmail: (val: boolean) => void;
  readonly setShowDevopsCarouselEnd: (val: boolean) => void;
  readonly setGoToURL: (val: string) => void;
  readonly enableCarouselEndCard: boolean;
};

export type GoToJSWType = {
  readonly isStaging: boolean;
  readonly jswName: string;
  readonly resendEmail?: ResendEmailScreenType;
};

export type GoToBitbucketType = {
  readonly isStaging: boolean;
  readonly resendEmail?: ResendEmailScreenType;
};

type PollForSuccessType = {
  readonly analyticsTrack: AnalyticsTrackType;
  readonly confirmationPageUrl: string;
  readonly checkEmailPageUrl: string;
  readonly isStaging: boolean;
  readonly wacEnvUrl: string;
  readonly isLoggedIn: boolean;
  readonly isNewUser?: boolean;
  readonly progressUri: string;
  readonly cloudName: string | null;
  readonly products: ProductPayloadObject[];
  readonly setSubmitError: (val: SubmitErrorType) => void;
  readonly setPollingLoaderShown: (val: boolean) => void;
  readonly handlePollProgressStatusFailure?: () => void;
  readonly isJswIncluded?: boolean | null;
  readonly isOpsgenieIncluded?: boolean | null;
  readonly dataRegion?: string | null;
  readonly resendEmail?: ResendEmailScreenType;
  readonly solutionStatsEvent?: UIAnalyticsEvent;
  readonly hideUserSegmentation?: boolean;
  readonly isAppProvisioningEligible?: boolean;
  readonly isBitbucketWorkspace?: boolean;
  readonly bitbucketWorkspaceUuid?: string;
};

export type FormPayloadActivateProducts = {
  activateProducts?: boolean;
  products: ProductPayloadObject[];
  cloudId?: string;
  cloudName: string | null;
  advancedSettings?: {
    [key: string]: number | boolean | string | Record<string, unknown>;
  };
  signupContext?: string;
};

export type FormPayloadType = {
  adminUsername: string;
  gRecaptchaResponse?: string;
  cloudName: string | null;
  email: string | null;
  timezone: string | null;
  firstName: string | null;
  lastName: string | null;
  adminPassword?: string | null;
  signupContext?: string;
  dataRegion?: string;
  consent: {
    site: string | null;
    locale: string | null;
    formUrl: string | null;
    consents: ConsentsObject[];
  };
} & FormPayloadActivateProducts;

const localStorageGet = (key: string): string => {
  try {
    return localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};

// N2N anon user
function goToCheckEmail(
  wacEnvUrl: string,
  checkEmailPageUrl: string,
  cloudName: string,
  products: string,
  analyticsTrack: AnalyticsTrackType,
  resendEmail?: ResendEmailScreenType
) {
  const splitPath = checkEmailPageUrl?.split('/');

  if (splitPath && splitPath.length >= 2 && splitPath[1].includes('wac')) {
    // remove contextual path if applicable
    splitPath.splice(0, 2);
    checkEmailPageUrl = `/${splitPath.join('/')}`;
  }

  const { newRelic, segment } = analyticsTrack;

  newRelic.action('apollo.signup.redirect-to-check-email', '');
  segment('bxp.formCloudSignup.navigatedToCheckEmail', {});

  const queryParams = {
    accountName: cloudName,
    products
  };

  // if (features.enableAutogenSiteName) {
  //   delete queryParams.accountName;
  // }
  // setResendEmailParams(cloudId, requestId);

  if (resendEmail?.enableResendEmail) {
    resendEmail.setShowResendEmail(true);
    return;
  }
  const urlParams = csvParams(queryParams);
  const nextUrl = `${wacEnvUrl}${checkEmailPageUrl}/?${urlParams}`;
  window.location.assign(nextUrl);
  return;
}

function goToBitbucket({ isStaging, resendEmail }: GoToBitbucketType) {
  const nextUrl = `${
    isStaging ? URL_BB_STAGE : URL_BB_PROD
  }/dashboard/overview`;
  if (resendEmail?.enableCarouselEndCard) {
    resendEmail.setGoToURL(nextUrl);
    return resendEmail.setShowDevopsCarouselEnd(true);
  }
  window.location.assign(nextUrl);
}

function goToJSW({ isStaging, jswName, resendEmail }: GoToJSWType) {
  const nextUrl = `https://${jswName}.${
    isStaging ? URL_JSW_STAGE : URL_JSW_PROD
  }`;
  if (resendEmail?.enableCarouselEndCard) {
    resendEmail.setGoToURL(nextUrl);
    return resendEmail.setShowDevopsCarouselEnd(true);
  }
  window.location.assign(nextUrl);
}

export function generateRenameUrl(
  wacEnvUrl: string,
  cloudId: string,
  progressUri: string,
  redirectUrl: string,
  siteRenamePageUrl: string,
  isPermitted?: boolean
) {
  const queryParams: any = {
    cloudId,
    requestId: getRequestId(progressUri),
    redirecturl: encodeURIComponent(redirectUrl),
    isPermitted
  };
  return `${wacEnvUrl}${siteRenamePageUrl}?${csvParams(queryParams)}`;
}

// Allow for product developers to connect to staging instance
export const isTestTenantByName = (
  isStaging: boolean,
  cloudName: string | null
) => {
  // testing tenant pattern: atl-vertigo--jira-jira-branch-foo--bar
  // short tenant pattern: atl-vertigo--jira-(jes/jed)-foo--bar
  const TEST_TENANT_PATTERN =
    /^atl-vertigo--(((?:jira|j|rolex|stg)+-jira-branch-[^-]+--)|(conf-(conf|stg)-proxy-\w+--[\w-]+)).*$/;
  const SHORT_TEST_TENANT_PATTERN = /^atl-vertigo--jira-(jes|jed)-[^-]+--.*$/;
  return (
    isStaging &&
    cloudName &&
    (TEST_TENANT_PATTERN.test(cloudName) ||
      SHORT_TEST_TENANT_PATTERN.test(cloudName))
  );
};

type GenerateConfirmationUrlOptions = {
  readonly wacEnvUrl: string;
  readonly confirmationPageUrl: string;
  readonly cloudId: string;
  readonly progressUri: string;
  readonly cloudName: string;
  readonly products: string;
  readonly isOpsgenieIncluded?: boolean | null;
  readonly dataRegion?: string | null;
  readonly analyticsTrack?: AnalyticsTrackType;
  readonly bbBundlePreferred?: boolean;
  readonly isDevops?: boolean;
  readonly isSimpleSignup?: boolean;
  readonly hideUserSegmentation?: boolean;
  readonly isAppProvisioningEligible?: boolean;
  readonly isNewUser?: boolean;
  readonly bitbucketWorkspaceUuid?: string;
};

// Generates the post-email confirmation url for verfied users
export function generateConfirmationUrl({
  wacEnvUrl,
  confirmationPageUrl,
  cloudId,
  progressUri,
  cloudName,
  products,
  isOpsgenieIncluded,
  dataRegion,
  analyticsTrack,
  bbBundlePreferred,
  isDevops,
  isSimpleSignup,
  hideUserSegmentation,
  isAppProvisioningEligible,
  isNewUser,
  bitbucketWorkspaceUuid
}: GenerateConfirmationUrlOptions) {
  // code for removing the /wac/ part of url if applicable
  const splitPath = confirmationPageUrl?.split('/');

  if (splitPath && splitPath.length >= 2 && splitPath[1].includes('wac')) {
    // remove contextual path if applicable
    splitPath.splice(0, 2);
    confirmationPageUrl = `/${splitPath.join('/')}`;
  }

  if (analyticsTrack) {
    const { newRelic, segment } = analyticsTrack;

    newRelic.action('apollo.signup.redirect-to-confirmation', '');
    segment('bxp.formCloudSignup.navigatedToConfirmation', {});
  }

  const constructedQueryParameters: { [key: string]: any } = {
    ondemandurl: cloudName,
    cloudId,
    requestId: getRequestId(progressUri)
  };

  if (bitbucketWorkspaceUuid !== undefined) {
    constructedQueryParameters.bitbucketWorkspaceUuid = bitbucketWorkspaceUuid;
  }

  constructedQueryParameters.products =
    products.length > 1 ? products : undefined;

  if (isOpsgenieIncluded) {
    constructedQueryParameters.dataRegion =
      dataRegion === 'EU' ? 'europe' : 'main';
  }

  const parametersFromUrl = Url.getUrlParams() || {};
  if (isDevops) {
    if (parametersFromUrl.hasOwnProperty('signupSource')) {
      constructedQueryParameters.signupSource = parametersFromUrl.signupSource;
    }
    constructedQueryParameters.isFoundingAdmin = true;
  }

  if (isSimpleSignup) {
    if (parametersFromUrl.hasOwnProperty('signupSource')) {
      constructedQueryParameters.signupSource = parametersFromUrl.signupSource;
    }
    constructedQueryParameters.isFoundingAdmin = true;
  }

  if (
    parametersFromUrl.hasOwnProperty('signupSource') &&
    parametersFromUrl.signupSource === 'confluenceBundle'
  ) {
    constructedQueryParameters.signupSource = parametersFromUrl.signupSource;
  }

  // Add `n2n` param for EXPCEN-3749
  if (parametersFromUrl.hasOwnProperty('n2n')) {
    constructedQueryParameters.n2n = parametersFromUrl.n2n;
  } else if (products.includes(ProductKeys.JIRA_SOFTWARE) && isNewUser) {
    constructedQueryParameters.n2n = true;
  }

  // Add `templateId` param to EXPCEN-3749
  if (parametersFromUrl.hasOwnProperty('templateId')) {
    constructedQueryParameters.templateId = parametersFromUrl.templateId;
  }

  // Add `team_type` param to SPICE-931
  if (parametersFromUrl.hasOwnProperty('team_type')) {
    constructedQueryParameters.team_type = parametersFromUrl.team_type;
  }

  // If BB is the preferred signup, it should be ordered first in the product param
  if (bbBundlePreferred) {
    constructedQueryParameters.products = constructedQueryParameters.products
      .split(',')
      .sort()
      .join();
  }

  // If this is a Jira Template signup, User Segmentation Screen should be hidden
  if (hideUserSegmentation) {
    constructedQueryParameters.hideUserSegmentation = true;
  }

  if (isAppProvisioningEligible === true) {
    constructedQueryParameters.provisionApp = true;
    const appKeyIncluded = constructedQueryParameters.products
      .split(',')
      .map((key: string) => {
        return Object.values(AppKey).includes(key as AppKey);
      });
    if (appKeyIncluded.includes(true)) {
      const indx = appKeyIncluded.indexOf(true);
      constructedQueryParameters.appKey =
        constructedQueryParameters.products.split(',')[indx];
    }
  }

  const nextUrl = `${wacEnvUrl}${confirmationPageUrl}/?${csvParams(
    constructedQueryParameters
  )}`;
  return nextUrl;
}

export function pollForSuccess({
  analyticsTrack,
  confirmationPageUrl,
  checkEmailPageUrl,
  isStaging,
  wacEnvUrl,
  isLoggedIn,
  isNewUser,
  progressUri,
  cloudName,
  products,
  setSubmitError,
  setPollingLoaderShown,
  isJswIncluded,
  solutionStatsEvent,
  isOpsgenieIncluded,
  dataRegion,
  resendEmail,
  hideUserSegmentation,
  isAppProvisioningEligible,
  isBitbucketWorkspace,
  bitbucketWorkspaceUuid,
  handlePollProgressStatusFailure = () => {}
}: PollForSuccessType) {
  const { segment, newRelic } = analyticsTrack;

  const signupSource = new URLSearchParams(window.location.search).get(
    'signupSource'
  );
  const bitbucketBundlePreferred = signupSource === 'bbbundle' ? true : false;

  let secondsPassed = 0;
  if (!cloudName) {
    trackProgressPollInvalid({ segment });
    return;
  }
  const poll = setInterval(async () => {
    secondsPassed++;
    if (secondsPassed === 15) {
      trackWaitedSignupPageSubmitMoreThan15SOptions({
        newRelic,
        segment
      });
      setSubmitError(SubmitErrorType.WARNING_TIMEOUT_PENDING);
    }
    if (secondsPassed === 60) {
      trackWaitedSignupPageSubmitMoreThan60S({
        newRelic,
        segment
      });
      setSubmitError(SubmitErrorType.ERROR_TIMEOUT);
      clearInterval(poll);
      setPollingLoaderShown(false);
    }
    const response = await fetch(`${progressUri}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    }).catch((err) => {
      clearInterval(poll);
      trackProgressUriErr({ err, newRelic, segment });
      return err;
    });

    trackProgressUri({ newRelic, response });
    const body = response.status === 200 ? await response.json() : null;
    if (!body) {
      clearInterval(poll);
      trackProgressPollMissing({ segment });
    }

    if (body) {
      const statusActivate = body.bestStatuses.ACTIVATE_PRODUCT;

      let statusSignup;
      if (isBitbucketWorkspace) {
        // move the user to the confirmation page if HAMS_CREATE_CLOUD status is SKIPPED
        if (body.bestStatuses.HAMS_CREATE_CLOUD) {
          statusSignup =
            body.bestStatuses.HAMS_CREATE_CLOUD === 'SKIPPED'
              ? 'COMPLETE_SUCCESS'
              : 'COMPLETE_ERROR';
        }
      } else {
        statusSignup =
          body.bestStatuses.HAMS_CREATE_CLOUD !== 'SKIPPED'
            ? body.bestStatuses.HAMS_CREATE_CLOUD
            : body.bestStatuses.BAG_CREATE_CLOUD;
      }

      if (body.cloudId && solutionStatsEvent) {
        if (solutionStatsEvent?.payload?.data?.isNewSiteProvisioned) {
          solutionStatsEvent.update({
            data: Object.assign({}, solutionStatsEvent?.payload?.data, {
              provisionedCloudId: body?.cloudId,
              targetCloudId: body?.cloudId
            })
          });
        }
        solutionStatsEvent.fire();
      }

      if (statusActivate && body.completed) {
        clearInterval(poll);

        if (body.successful) {
          trackSubmitSuccess({ isLoggedIn, segment });

          if (isJswIncluded || bitbucketBundlePreferred) {
            trackGoToBitbucket({ newRelic });
            return goToBitbucket({ isStaging, resendEmail });
          } else {
            trackGoToJSW({ newRelic });
            // Give JSW an extra second before redirect
            return setTimeout(
              () => goToJSW({ isStaging, jswName: cloudName, resendEmail }),
              1000
            );
          }
        } else {
          trackProgressPollError({
            label: 'body',
            segment
          });
        }
        // TODO: else if successful === false, throw error/show in UI
      }

      switch (statusSignup) {
        case 'COMPLETE_SUCCESS':
        case 'COMPLETE_MANUAL_SUCCESS':
          clearInterval(poll);

          const cloudId = isBitbucketWorkspace ? body.orgId : body.cloudId;
          const productParam = products.map((p) => p.product).join(',');

          // TODO if (!cloudId) throw Error;
          if (!cloudId) {
            trackProgressPollMissing({ segment });
          } else {
            trackSubmitSuccessProducts({
              products,
              isLoggedIn,
              segment
            });
          }

          if (isLoggedIn) {
            const confirmURL = generateConfirmationUrl({
              wacEnvUrl,
              confirmationPageUrl,
              cloudId,
              progressUri,
              cloudName,
              products: productParam,
              isOpsgenieIncluded,
              dataRegion,
              analyticsTrack,
              bbBundlePreferred: bitbucketBundlePreferred,
              hideUserSegmentation,
              isAppProvisioningEligible,
              isNewUser,
              bitbucketWorkspaceUuid
            });
            // devops carousel
            if (resendEmail?.enableCarouselEndCard) {
              resendEmail.setShowDevopsCarouselEnd(true);
              return resendEmail.setGoToURL(confirmURL);
            }
            return window.location.assign(confirmURL);
          } else {
            return goToCheckEmail(
              wacEnvUrl,
              checkEmailPageUrl,
              cloudName,
              productParam,
              analyticsTrack,
              resendEmail
            );
          }

        case 'COMPLETE_ERROR':
          clearInterval(poll);
          trackProgressPollError({
            label: statusSignup,
            segment
          });
          // TODO: show error on frontend

          if (isBitbucketWorkspace) {
            setSubmitError(SubmitErrorType.ERROR_PRODUCT_ACTIVATE);
            setPollingLoaderShown(false);
            handlePollProgressStatusFailure();
          }
          return;
      }
    }
  }, 1000);
}

export async function formSubmitN2N({
  payload,
  wacEnvUrl,
  confirmationPageUrl,
  checkEmailPageUrl,
  isStaging,
  isLoggedIn,
  isJswIncluded,
  solutionStatsEvent,
  isOpsgenieIncluded,
  dataRegion,
  setPollingLoaderShown,
  analyticsTrack,
  setSubmitError,
  resendEmail,
  hideUserSegmentation,
  isAppProvisioningEligible
}: FormSubmitN2NType) {
  const { segment, newRelic } = analyticsTrack;
  setPollingLoaderShown(true);

  const submitUrl = isLoggedIn
    ? `https://${
        isStaging ? 'wac.stg.internal.' : 'www.'
      }atlassian.com/gateway/api/cloud/signup`
    : `${wacEnvUrl}/endpoint/cloud/signupAnon`;
  try {
    const response = await fetch(submitUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'atl-cookies': JSON.stringify({
          _ga: Cookies.get('_ga') || '',
          seg_xid: localStorageGet('seg_xid'),
          __atl_path: Cookies.get('__atl_path') || '',
          ajs_anonymous_id: Cookies.get('ajs_anonymous_id') || ''
        })
      },
      body: JSON.stringify(payload),
      credentials: 'include'
    });

    if (response.ok && response.status === 200) {
      newRelic.trackAjax('cofs_create_cloud_bxp_react', response, true);
      const body = await response.json();

      // when site name creation is differed the payload wont have a cloudName to pass to the poll function
      // the response from the fetch will include the auto gen site name to be used as the cloudName
      // without a cloudName it wont redirect to the email confirmation or the rename page
      let cloudName = null;
      if (payload?.signupContext?.match('autogenSiteName')) {
        cloudName = body.cloudName;
      } else {
        cloudName = payload.cloudName;
      }

      // set r.e.d session key for reading by email verification component
      if ('email' in payload) {
        // ensure payload is type FormPayloadType
        const resendData = Object.assign(
          {},
          {
            cloudName: encodeURIComponent(cloudName || ''),
            cloudId: body.cloudId || '',
            requestId: body.progressUri ? getRequestId(body.progressUri) : '',
            email: encodeURIComponent(payload.email || ''),
            primaryProduct: encodeURIComponent(
              payload?.products[0]?.product || ''
            )
          }
        );
        sessionStorage.setItem(
          ResendEmailType.RESEND_DATA,
          window.btoa(JSON.stringify(resendData))
        );
      }

      pollForSuccess({
        isLoggedIn: isLoggedIn || false,
        confirmationPageUrl,
        checkEmailPageUrl,
        progressUri: body.progressUri,
        products: payload.products,
        analyticsTrack,
        isStaging,
        wacEnvUrl,
        cloudName,
        setSubmitError,
        setPollingLoaderShown,
        isJswIncluded,
        isOpsgenieIncluded,
        dataRegion,
        resendEmail,
        solutionStatsEvent,
        hideUserSegmentation,
        isAppProvisioningEligible
      });

      return response;
    }
    if (!response.ok || response.status !== 200) {
      let body = '';
      try {
        body = await response.json();
      } catch (e) {
        body = 'Unable to parse response as JSON';
        sessionStorage.removeItem(ResendEmailType.RESEND_DATA);
      }
      newRelic.trackAjax('cofs_create_cloud_bxp_react', response, false);
      segment('bxp.formCloudSignup.submitError', {
        type: isLoggedIn ? 'N2E' : 'N2N',
        body
      });
      setPollingLoaderShown(false);
      setSubmitError(SubmitErrorType.ERROR_TIMEOUT);
      return Promise.reject(response);
    }
    return Promise.reject();
  } catch (error) {
    setPollingLoaderShown(false);
    setSubmitError(SubmitErrorType.ERROR_TIMEOUT);
    newRelic.trackAjax('cofs_create_cloud_bxp_react', error, false);
    segment('bxp.formCloudSignup.submitError', {
      type: isLoggedIn ? 'N2E' : 'N2N',
      error: JSON.stringify(error)
    });
    sessionStorage.removeItem(ResendEmailType.RESEND_DATA);
    return Promise.reject(error);
  }
}

function csvParams(queryParams: any) {
  return Object.keys(queryParams)
    .map((key) => `${key}=${queryParams[key]}`)
    .join('&');
}

function getRequestId(progressUri: string) {
  const pattern = new RegExp(/(cloud|organization)\/[\w-]+\/status\/([\w-]+)$/);
  const matches = pattern.exec(progressUri);
  return matches ? matches[2] : '';
}
